import { useEffect, useRef } from 'react';

// How close (px) the pointer has to get to a card's border before it starts to
// glow, and the size of the light blob that follows the pointer.
const REACH = 90;
const BLOB = 280;
// Smaller blocks are chips and buttons, not cards.
const MIN_W = 160;
const MIN_H = 72;
// The card list is cheap but not free: refresh it at most this often.
const REFRESH_MS = 600;

// A "card" here is the site's own recipe (DESIGN.md): a rounded block with a
// hairline border on a surface fill. Cards that carry their own hover glow,
// menus, skeletons and anything floating are left alone. Links are NOT
// skipped: the project cards on the home page are anchors, and the minimum
// size already keeps small link buttons out.
const OWN_GLOW = '[class*="card-glow"]';
const SKIP = '[role="menu"], [role="tooltip"], [data-no-edge-glow], button, input, select, textarea';

function isCard(el) {
  const c = el.classList;
  if (!(c.contains('bg-surface') || c.contains('bg-surface/40') || c.contains('bg-surface/60'))) return false;
  if (![...c].some((n) => n.startsWith('rounded-'))) return false;
  if (c.contains('absolute') || c.contains('fixed') || c.contains('animate-pulse')) return false;
  if (el.matches(SKIP)) return false;
  // The project cards with their own brand glow (Patrimony, Gabremoku, Velum)
  // keep it: the glow class can sit on the card itself or on a wrapper/child.
  return !el.closest(OWN_GLOW) && !el.querySelector(OWN_GLOW);
}

/**
 * When the pointer gets near the border of a card, a soft emerald light gathers
 * on the nearest point of that card's edge and follows the pointer along it.
 *
 * One fixed overlay serves every card, so no card needs touching: the overlay
 * takes the box and corner radius of whichever card is nearest. Only `opacity`
 * and `transform` change per frame (a ring-shaped mask shows the border band, a
 * blob slides under it), and both are eased towards their target, so the glow
 * swells and drifts instead of switching on. Off for touch and reduced motion.
 */
export default function CardEdgeGlow() {
  const rootRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia && (window.matchMedia('(pointer: coarse)').matches
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches)) return undefined;

    const root = rootRef.current;
    const ring = ringRef.current;
    if (!root || !ring) return undefined;

    const pointer = { x: -1000, y: -1000, seen: false };
    const pos = { x: -1000, y: -1000 };
    let cards = [];
    let cardsAt = 0;
    let target = null;      // card element the overlay is currently shaped like
    let shapeW = 0;
    let shapeH = 0;
    let opacity = 0;
    let raf = 0;
    let idleFrames = 0;

    const refreshCards = (now) => {
      if (now - cardsAt < REFRESH_MS) return;
      cardsAt = now;
      cards = [...document.querySelectorAll('.border-line')].filter(isCard);
    };

    // Distance from the pointer to the card's border, whether it is inside the
    // card (towards the nearest side) or just outside it.
    const edgeDistance = (r, x, y) => {
      const inside = x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      if (inside) return Math.min(x - r.left, r.right - x, y - r.top, r.bottom - y);
      const dx = Math.max(r.left - x, 0, x - r.right);
      const dy = Math.max(r.top - y, 0, y - r.bottom);
      return Math.hypot(dx, dy);
    };

    const frame = (now) => {
      raf = 0;
      let best = null;
      let bestRect = null;
      let bestD = REACH;

      if (pointer.seen && !document.hidden) {
        refreshCards(now);
        for (const el of cards) {
          const r = el.getBoundingClientRect();
          if (r.width < MIN_W || r.height < MIN_H) continue;
          if (r.bottom < 0 || r.top > window.innerHeight) continue;
          const d = edgeDistance(r, pointer.x, pointer.y);
          if (d < bestD) { bestD = d; best = el; bestRect = r; }
        }
      }

      const want = best ? Math.pow(1 - bestD / REACH, 1.4) : 0;
      opacity += (want - opacity) * 0.14;

      if (best) {
        if (best !== target) {
          target = best;
          root.style.borderRadius = getComputedStyle(best).borderTopLeftRadius;
        }
        if (bestRect.width !== shapeW || bestRect.height !== shapeH) {
          shapeW = bestRect.width;
          shapeH = bestRect.height;
          root.style.width = `${shapeW}px`;
          root.style.height = `${shapeH}px`;
        }
        // The light trails the pointer a little, which is what gives it motion.
        pos.x += (pointer.x - pos.x) * 0.22;
        pos.y += (pointer.y - pos.y) * 0.22;
        root.style.transform = `translate3d(${bestRect.left}px, ${bestRect.top}px, 0)`;
        ring.style.transform = `translate3d(${pos.x - bestRect.left - BLOB / 2}px, ${pos.y - bestRect.top - BLOB / 2}px, 0)`;
      }
      root.style.opacity = opacity < 0.004 ? '0' : opacity.toFixed(3);

      // Keep running while something is still easing or the pointer is close;
      // otherwise stop and wait for the next movement.
      if (best || opacity > 0.004) {
        idleFrames = 0;
        raf = requestAnimationFrame(frame);
      } else if (++idleFrames < 2) {
        raf = requestAnimationFrame(frame);
      }
    };

    const wake = () => { if (!raf) raf = requestAnimationFrame(frame); };

    const onMove = (e) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      if (!pointer.seen) { pos.x = e.clientX; pos.y = e.clientY; }
      pointer.seen = true;
      wake();
    };
    const onLeave = () => { pointer.seen = false; wake(); };

    // Cards slide under a still pointer when the page scrolls.
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('scroll', wake, { passive: true });
    document.documentElement.addEventListener('mouseleave', onLeave);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('scroll', wake);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div ref={rootRef} className="edge-glow" aria-hidden="true">
      <div className="edge-glow__ring"><span ref={ringRef} className="edge-glow__blob" /></div>
    </div>
  );
}
